import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import type { InputProps } from "./Input.types";
import Icon from "../icon/Icon";
import "./input.scss";

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  iconName,
  placeholder,
  maxLength,
  clear,
  disabled,
  error,
  success,
  errorMessage,
  onClick,
  onChange,
  value,
  style,
  className,
  iconInput,
  name,  
}, ref) => {
  const [content, setContent] = useState(value ?? "");
  const isExccessive = !!maxLength && content.length >= maxLength;
  const isClear = clear ? "sb-input--clear" : "";
  const isDisabled = disabled ? "sb-input--disabled" : "sb-input--enabled";
  const isError = isExccessive || error ? "sb-input--error" : "";
  const isSuccess = success ? "sb-input--success" : "";

  useEffect(() => {
    if (!!value) {
      setContent(value);
    }
  }, [value]);

  return (
    <div
      className={[
        "sb-input",
        isDisabled,
        isError,
        isClear,
        isSuccess,
        className,
      ].join(" ")}
      style={{ ...style }}
      data-testid="sb-input"
    >
      {label && (
        <div
          className="sb-input__label flex mb-xsm"
          data-testid="sb-input__label"          
        >
          {iconName && (
            <Icon iconName={iconName} size={13} className="mr-xsm" />
          )}
          <span>{label}</span>
        </div>
      )}
      <div
        className={iconInput ? "sb-input__icon" : ""}
        data-testid="sb-input__icon"
      >
        <input
          className="sb-input__container"
          data-testid="sb-input__container"
          onChange={(event: any) => {
            setContent(event.target.value);
            onChange && onChange(event);
          }}
          placeholder={placeholder}
          disabled={disabled}
          onClick={onClick}
          value={content}
          name={name}
          ref={ref}
        />
        {iconInput && (
          <Icon iconName={iconInput} size={18} className="text-primary-60" />
        )}
      </div>
      <div
        className={`flex text-small-02 ${
          isExccessive || error ? "justify-between" : "justify-end"
        } ${error && "mt-xsm"}`}
      >
        {(isExccessive || error) && (
          <p className="flex text-error mr-sm">
            <Icon
              iconName="alert-triangle"
              size={13}
              className="text-error mr-xsm"
            />
            {isExccessive ? "Excediste el número de caracteres" : errorMessage}
          </p>
        )}
        {!!maxLength && (
          <span
            className={`sb-input-counter ${
              isExccessive ? "text-error" : "text-neutral-01"
            }`}
          >
            {isExccessive && "-"}
            {content.length}/{maxLength}
          </span>
        )}
      </div>
    </div>
  );
});


Input.propTypes = {
  /**
   * label
   */
  label: PropTypes.string,
  /**
   * iconName
   */
  iconName: PropTypes.string,
  /**
   * iconInput
   */
  iconInput: PropTypes.string,
  /**
   * value
   */
  value: PropTypes.string,
  /**
   * placeholder
   */
  placeholder: PropTypes.string,
  /**
   * maxLength
   */
  maxLength: PropTypes.number,
  /**
   * is clear?
   */
  clear: PropTypes.bool,
  /**
   * disabled?
   */
  disabled: PropTypes.bool,
  /**
   * error?
   */
  error: PropTypes.bool,
  /**
   * errorMessage
   */
  errorMessage: PropTypes.string,
  /**
   * success?
   */
  success: PropTypes.bool,
  /**
   * onClick
   */
  onClick: PropTypes.func,
  /**
   * onChange
   */
  onChange: PropTypes.func,
  /**
   * style
   */
  style: PropTypes.object,
  /**
   * className
   */
  className: PropTypes.string,
  /**
   * name
   */
  name: PropTypes.string, 
};

Input.defaultProps = {
  label: "",
  iconName: "check",
  placeholder: "",
  value: "",
  maxLength: 0,
  disabled: false,
  success: false,
  error: false,
  errorMessage: "Error",
  clear: false,
  style: {},
  className: "",
  onClick: () => {},
  onChange: () => {},
  name: "",  
};

export default Input;
